import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { CURRENT_NETWORK } from "../config/networks";
import {
  STORAGE_ADDRESS, STORAGE_ABI,
  LOGIC_ADDRESS, LOGIC_ABI,
  USDT_ADDRESS, USDT_ABI,
} from "../config/contract";
import { getInjectedProvider, detectWallet, isMobile, openInTrustWallet } from "../utils/wallet";
import { useNotification } from "../hooks/useNotification";
import { parseError } from "../utils/errorParser";

const Web3Context = createContext(null);

export function Web3Provider({ children }) {
  const notify = useNotification();

  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [walletName, setWalletName] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [roles, setRoles] = useState({ isOwner: false, isAdmin: false, ownerAddr: "", adminAddr: "" });

  // Providers / contracts kept on refs — mutating them shouldn't trigger re-renders
  const providerRef = useRef(null);
  const signerRef = useRef(null);
  const readProviderRef = useRef(null);
  const storageRef = useRef(null); // storage contract (reads + admin state writes)
  const logicRef = useRef(null);   // logic contract (business writes)
  const usdtRef = useRef(null);
  const injectedRef = useRef(null);

  // Read-only provider (works even without wallet)
  useEffect(() => {
    try {
      readProviderRef.current = new ethers.providers.JsonRpcProvider(
        CURRENT_NETWORK.rpcUrls[0],
        { chainId: CURRENT_NETWORK.chainId, name: CURRENT_NETWORK.chainName }
      );
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build contract instances whenever account/chain/signer changes
  const rebuildContracts = () => {
    const signer = signerRef.current;
    const read = readProviderRef.current;
    const runner = signer || read;
    if (runner && STORAGE_ADDRESS && STORAGE_ADDRESS !== ethers.constants.AddressZero) {
      storageRef.current = new ethers.Contract(STORAGE_ADDRESS, STORAGE_ABI, runner);
    } else {
      storageRef.current = null;
    }
    if (runner && LOGIC_ADDRESS && LOGIC_ADDRESS !== ethers.constants.AddressZero) {
      logicRef.current = new ethers.Contract(LOGIC_ADDRESS, LOGIC_ABI, runner);
    } else {
      logicRef.current = null;
    }
    if (runner && USDT_ADDRESS && USDT_ADDRESS !== ethers.constants.AddressZero) {
      usdtRef.current = new ethers.Contract(USDT_ADDRESS, USDT_ABI, runner);
    } else {
      usdtRef.current = null;
    }
  };

  // ============================================================
  // Chain switching — no refresh
  // ============================================================
  const switchNetwork = async () => {
    const injected = injectedRef.current || getInjectedProvider();
    if (!injected) {
      notify.error("No wallet detected");
      return false;
    }
    setIsSwitching(true);
    try {
      await injected.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: CURRENT_NETWORK.chainIdHex }],
      });
      notify.success("Network switched");
      return true;
    } catch (err) {
      // 4902 = chain not added; some wallets return -32603 with data.originalError.code = 4902
      const code = err.code || err.data?.originalError?.code;
      if (code === 4902 || code === -32603) {
        try {
          await injected.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: CURRENT_NETWORK.chainIdHex,
                chainName: CURRENT_NETWORK.chainName,
                nativeCurrency: CURRENT_NETWORK.nativeCurrency,
                rpcUrls: CURRENT_NETWORK.rpcUrls,
                blockExplorerUrls: CURRENT_NETWORK.blockExplorerUrls,
              },
            ],
          });
          notify.success("Network added and switched");
          return true;
        } catch (addErr) {
          notify.error(parseError(addErr));
          return false;
        }
      }
      notify.error(parseError(err));
      return false;
    } finally {
      setIsSwitching(false);
    }
  };

  // ============================================================
  // Wallet listeners — set once, use refs to avoid re-registration loops
  // ============================================================
  const applyChainId = async (rawChainId) => {
    let id;
    if (typeof rawChainId === "string") id = parseInt(rawChainId, 16);
    else if (typeof rawChainId === "number") id = rawChainId;
    else if (rawChainId?.toNumber) id = rawChainId.toNumber();
    else id = Number(rawChainId);
    setChainId(id);
  };

  const handleAccountsChanged = async (accounts) => {
    if (!accounts || accounts.length === 0) {
      disconnect();
      return;
    }
    setAccount(ethers.utils.getAddress(accounts[0]));
    // Rebuild signer with new account
    if (providerRef.current) {
      signerRef.current = providerRef.current.getSigner();
      rebuildContracts();
    }
  };

  const handleChainChanged = async (hexChainId) => {
    applyChainId(hexChainId);
    // Rebuild web3Provider so it sees the new chain — but do NOT refresh page
    const injected = injectedRef.current;
    if (injected) {
      providerRef.current = new ethers.providers.Web3Provider(injected, "any");
      if (account) {
        signerRef.current = providerRef.current.getSigner();
      }
      rebuildContracts();
    }
  };

  const attachListeners = (injected) => {
    if (!injected || !injected.on) return;
    // Remove any previously attached before re-attaching
    try {
      injected.removeListener?.("accountsChanged", handleAccountsChanged);
      injected.removeListener?.("chainChanged", handleChainChanged);
    } catch {}
    injected.on("accountsChanged", handleAccountsChanged);
    injected.on("chainChanged", handleChainChanged);
  };

  // ============================================================
  // Connect
  // ============================================================
  const connect = async () => {
    if (isConnecting) return;
    const injected = getInjectedProvider();
    if (!injected) {
      // On mobile with no injected wallet, open in Trust Wallet browser
      if (isMobile()) {
        openInTrustWallet();
        return;
      }
      notify.error("No wallet detected. Please install Trust Wallet or MetaMask.");
      return;
    }
    setIsConnecting(true);
    try {
      injectedRef.current = injected;
      const accounts = await injected.request({ method: "eth_requestAccounts" });
      if (!accounts || accounts.length === 0) throw new Error("No accounts returned");

      providerRef.current = new ethers.providers.Web3Provider(injected, "any");
      signerRef.current = providerRef.current.getSigner();

      const wname = detectWallet();
      setWalletName(wname);

      const currentAccount = ethers.utils.getAddress(accounts[0]);
      setAccount(currentAccount);

      // Fetch current chain
      const rawChainId = await injected.request({ method: "eth_chainId" });
      applyChainId(rawChainId);

      attachListeners(injected);
      rebuildContracts();

      const numericChain = parseInt(rawChainId, 16);
      if (numericChain !== CURRENT_NETWORK.chainId) {
        notify.info("Switching to the correct network...");
        await switchNetwork();
      } else {
        notify.success(`${wname} connected`);
      }
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = () => {
    setAccount(null);
    setChainId(null);
    setWalletName("");
    signerRef.current = null;
    providerRef.current = null;
    rebuildContracts();
    notify.info("Wallet disconnected");
  };

  // ============================================================
  // Silent connect on load if wallet is already authorized
  // ============================================================
  useEffect(() => {
    const tryEagerConnect = async () => {
      const injected = getInjectedProvider();
      if (!injected) return;
      try {
        const accounts = await injected.request({ method: "eth_accounts" });
        if (accounts && accounts.length > 0) {
          injectedRef.current = injected;
          providerRef.current = new ethers.providers.Web3Provider(injected, "any");
          signerRef.current = providerRef.current.getSigner();
          setAccount(ethers.utils.getAddress(accounts[0]));
          setWalletName(detectWallet());
          const rawChainId = await injected.request({ method: "eth_chainId" });
          applyChainId(rawChainId);
          attachListeners(injected);
          rebuildContracts();
        }
      } catch {
        // ignore
      }
    };
    tryEagerConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // Auto-switch network when connected on wrong chain
  // ============================================================
  const autoSwitchTriedRef = useRef(false);
  useEffect(() => {
    if (!account || !chainId) return;
    if (chainId === CURRENT_NETWORK.chainId) {
      autoSwitchTriedRef.current = false;
      return;
    }
    if (autoSwitchTriedRef.current) return;
    autoSwitchTriedRef.current = true;
    notify.warning("Wrong network — switching automatically...");
    switchNetwork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, chainId]);

  const isCorrectChain = chainId === CURRENT_NETWORK.chainId;

  // ============================================================
  // Role detection — resolve owner / admin addresses from contract
  // ============================================================
  useEffect(() => {
    let cancelled = false;
    const detectRoles = async () => {
      const contract = storageRef.current;
      if (!contract || !account || !isCorrectChain) {
        setRoles({ isOwner: false, isAdmin: false, ownerAddr: "", adminAddr: "" });
        return;
      }
      try {
        const [ownerAddr, adminAddr] = await Promise.all([
          contract.owner(),
          contract.admin(),
        ]);
        if (cancelled) return;
        const acc = account.toLowerCase();
        setRoles({
          isOwner: ownerAddr.toLowerCase() === acc,
          isAdmin: adminAddr.toLowerCase() === acc,
          ownerAddr,
          adminAddr,
        });
      } catch {
        if (!cancelled) {
          setRoles({ isOwner: false, isAdmin: false, ownerAddr: "", adminAddr: "" });
        }
      }
    };
    detectRoles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, chainId, isCorrectChain]);

  const value = {
    account,
    chainId,
    walletName,
    isConnecting,
    isSwitching,
    isCorrectChain,
    isOwner: roles.isOwner,
    isAdmin: roles.isAdmin,
    hasAdminAccess: roles.isOwner || roles.isAdmin,
    ownerAddr: roles.ownerAddr,
    adminAddr: roles.adminAddr,
    connect,
    disconnect,
    switchNetwork,
    // NEW: two separate contract accessors
    getStorage: () => storageRef.current,
    getLogic: () => logicRef.current,
    // Legacy alias — `getContract()` returns Storage (most reads work off it).
    // Callers that write business logic (register/activate/claim/block) should use getLogic().
    getContract: () => storageRef.current,
    getUsdt: () => usdtRef.current,
    getSigner: () => signerRef.current,
    getReadProvider: () => readProviderRef.current,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}

export function useWeb3() {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error("useWeb3 must be inside Web3Provider");
  return ctx;
}
